import type {BudgetLine, BudgetStatus} from './api/types';

export interface BudgetSummary {
    tone: 'muted' | 'warning' | 'danger';
    headline: string;
    detail?: string;
}

export function formatUsd(amount: number): string {
    if (amount > 0 && amount < 0.005) {
        return '< USD 0.01';
    }
    return `USD ${amount.toFixed(2)}`;
}

const SCOPE_LABELS: Record<string, string> = {
    trial: 'trial allowance',
    platformMonthly: "this month's platform allowance",
    customerMonthly: "your organisation's monthly allowance",
    person: 'your monthly allowance'
};

export function describeLine(line: BudgetLine): string {
    const label = SCOPE_LABELS[line.scope] ?? 'allowance';
    return `${formatUsd(line.spentUsd)} of ${formatUsd(line.allowanceUsd)} ${label} used (${line.percent}%)`;
}

/**
 * What the panel's banner shows for a budget status: nothing when no allowance is
 * in play; a muted line while under the warning level; a warning or danger alert
 * with the server's reason otherwise. The line shown is the one that set the
 * level, else the fullest.
 */
export function describeBudget(
    budget: BudgetStatus | null | undefined
): BudgetSummary | null {
    if (!budget || budget.lines.length === 0) {
        return null;
    }

    const trigger =
        budget.lines.find(line => line.scope === budget.scope) ??
        [...budget.lines].sort((a, b) => b.percent - a.percent)[0];

    if (budget.level === 'exhausted') {
        return {
            tone: 'danger',
            headline: budget.reason ?? 'The AI allowance is used up.',
            detail: describeLine(trigger)
        };
    }

    if (budget.level === 'warning') {
        return {
            tone: 'warning',
            headline: budget.reason ?? describeLine(trigger),
            detail:
                budget.lines.length > 1
                    ? budget.lines
                          .filter(line => line !== trigger)
                          .map(describeLine)
                          .join('; ')
                    : undefined
        };
    }

    return {tone: 'muted', headline: describeLine(trigger)};
}
